import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, TextField, Typography } from '@mui/material';
import { parse, isValid, isAfter, startOfDay } from 'date-fns';
import { z } from 'zod';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { LdtOrderFormHeader } from '@ldt/features/orders/components/order_form_header';
import { OrderingProviderFormSection } from '@ldt/features/orders/components/ordering_provider_form_section';
import {
  PatientInformationFormSchema,
  PatientInformationFormSection,
} from '@ldt/features/orders/components/patient_information_form_section';
import { useOrderingProviderFormSection } from '@ldt/features/orders/hooks/use_ordering_provider_form_section';
import { OrderingProviderForm } from '@ldt/features/orders/ordering_provider_form';
import { FormDatePicker } from 'src/components/date_picker';
import { DecimalInput } from 'src/components/decimal_input';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Caution } from 'src/components/icons/caution';
import { SectionHeader } from 'src/components/section_header';
import { SelectList } from 'src/components/select_list';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import {
  processFetcherResponse,
  getProstateBiopsyTStageOptions,
  PROSTATE_BIOPSY_T_STAGES,
  safeEmptyToUndefined,
} from 'src/distribution_platforms/features/order_form';
import { hasSemanticFormChanges } from 'src/distribution_platforms/features/utils/patient_utils';
import { useDebounce } from 'src/hooks/use_debounce';
import {
  Ethnicity,
  LdtOrderForReviseReportFieldsFragment,
  ReviseReportInput,
  SpecimenType,
  TStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import { notifyError, notifySuccess, SpecimenTypeUtils } from 'src/utils';

const FORM_CONSTANTS = {
  PSA_MIN: 0,
} as const;

/** Field keys excluded from "must revise at least 1 field" check */
const FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK = new Set<string>([
  'revisionReason',
]);

export type LdtMetastaticProstateReviseReportFormData = z.infer<
  ReturnType<typeof createLdtMetastaticProstateReviseReportFormSchema>
>;

export const createLdtMetastaticProstateReviseReportFormSchema = (
  isClinicWorkflow: boolean,
) =>
  z.object({
    ...PatientInformationFormSchema.shape,

    orderingProviderId: z.string().min(1, 'Ordering Provider is required'),

    baselinePsa: z
      .number({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return { message: 'Most Recent Pre-Treatment PSA is required' };
          }
          return null;
        },
      })
      .min(
        FORM_CONSTANTS.PSA_MIN,
        `PSA must be at least ${FORM_CONSTANTS.PSA_MIN} ng/mL`,
      )
      .nullable()
      .superRefine((val, ctx) => {
        if (val === null) {
          ctx.addIssue({
            code: 'custom',
            message: 'Most Recent Pre-Treatment PSA is required',
          });
        }
      }),

    tStage: z.enum(PROSTATE_BIOPSY_T_STAGES as [TStage, ...TStage[]], {
      error: 'Clinical T-stage is required',
    }),

    dateOfBiopsy: z
      .string()
      .min(
        1,
        `${SpecimenTypeUtils.getSpecimenTypeInputLabel(SpecimenType.MetastaticProstateBiopsy)} is required`,
      )
      .refine(
        (val) => {
          if (!val || !val.trim()) return false;
          try {
            const date = parse(val.trim(), 'yyyy-MM-dd', new Date());
            if (!isValid(date)) return false;
            return !isAfter(date, startOfDay(new Date()));
          } catch {
            return false;
          }
        },
        {
          message: `${SpecimenTypeUtils.getSpecimenTypeInputLabel(
            SpecimenType.MetastaticProstateBiopsy,
          )} cannot be a future date`,
        },
      ),

    // Specimen ID: required for Clinic, not shown for Tempus
    specimenId: isClinicWorkflow
      ? z
          .string()
          .min(1, 'Specimen ID is required')
          .regex(
            /^[a-zA-Z0-9\-_.(),:; /]+$/,
            'Allowed characters: alphanumeric, dash, underscore, dot, comma, parentheses, colon, semicolon, space, slash',
          )
      : z.string().optional(),

    revisionReason: z
      .string()
      .min(1, 'Revision Reason is required')
      .max(400, 'Revision Reason must be at most 400 characters'),
  });

export const createMetastaticProstateReviseReportCustomResolver =
  (
    isClinicWorkflow: boolean,
    warningsRef?: {
      current: Partial<
        Record<keyof LdtMetastaticProstateReviseReportFormData, string>
      >;
    },
  ) =>
  async (
    data: LdtMetastaticProstateReviseReportFormData,
    context: unknown,
    options: ResolverOptions<LdtMetastaticProstateReviseReportFormData>,
  ) => {
    const schema =
      createLdtMetastaticProstateReviseReportFormSchema(isClinicWorkflow);
    const zodResult = await zodResolver(schema)(data, context, options);

    const customErrors = { ...zodResult.errors };
    const warnings: Partial<
      Record<keyof LdtMetastaticProstateReviseReportFormData, string>
    > = {};

    // PSA warning when above 100
    if (data.baselinePsa && data.baselinePsa > 100) {
      warnings.baselinePsa =
        'PSA exceeds 100.00 ng/mL. Please verify that the PSA is correct before proceeding.';
    }

    const result = {
      values: zodResult.values,
      errors: customErrors,
      warnings,
    } as ResolverResult<LdtMetastaticProstateReviseReportFormData> & {
      warnings: Partial<
        Record<keyof LdtMetastaticProstateReviseReportFormData, string>
      >;
    };

    if (warningsRef) {
      warningsRef.current = result.warnings;
    }

    return result;
  };

export const createMetastaticProstateReviseReportDefaultFormValues = (
  order: LdtOrderForReviseReportFieldsFragment,
): LdtMetastaticProstateReviseReportFormData => ({
  firstName: order?.patient?.firstNamePhi?.trim() ?? '',
  lastName: order?.patient?.lastNamePhi?.trim() ?? '',
  dateOfBirth: order?.patient?.dateOfBirthPhi ?? '',
  ethnicity: (order?.patient?.ethnicity ?? '') as Ethnicity,
  orderingProviderId: order?.orderingProvider?.id ?? '',
  baselinePsa: (order?.psa ?? '') as number,
  tStage: (order?.tStage ?? '') as TStage,
  specimenId: order?.specimenId?.trim() ?? '',
  dateOfBiopsy: order?.labProcedureDatePhi ?? '',
  revisionReason: '',
});

export const LdtMetastaticProstateReviseReportForm: FC<{
  order: LdtOrderForReviseReportFieldsFragment;
  ldtOrderRepository: LdtOrderRepository;
}> = ({ order, ldtOrderRepository }) => {
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const location = useLocation();
  const workflowType = useWorkflowType();
  const isClinicWorkflow = workflowType === WorkflowType.CLINIC;

  const warningsRef = useRef<
    Partial<Record<keyof LdtMetastaticProstateReviseReportFormData, string>>
  >({});

  const defaultValues =
    createMetastaticProstateReviseReportDefaultFormValues(order);

  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<LdtMetastaticProstateReviseReportFormData>({
    resolver: createMetastaticProstateReviseReportCustomResolver(
      isClinicWorkflow,
      warningsRef,
    ),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const {
    selectedOrderingProvider,
    hasOrderingProviderChanges,
    orderingProviderSectionProps,
    orderingProviderDialogProps,
  } = useOrderingProviderFormSection({
    ldtOrderRepository,
    isOrderingPhysicianUser: false,
    initialOrderingProvider: order.orderingProvider ?? null,
    setOrderingProviderId: (id, options) =>
      setValue('orderingProviderId', id, options),
    triggerOrderingProviderValidation: () => trigger('orderingProviderId'),
  });

  const currentValues = watch();

  // Validate all non-excluded fields on mount so warnings display immediately.
  useEffect(() => {
    const fieldsToValidate = (
      Object.keys(
        defaultValues,
      ) as (keyof LdtMetastaticProstateReviseReportFormData)[]
    ).filter((name) => !FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK.has(name));
    void trigger(fieldsToValidate);
    // defaultValues is stable for the lifetime of the form (derived from order on mount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const debouncedPsaValidation = useDebounce(() => {
    trigger('baselinePsa');
  }, 300);

  const getReviseReportInputFromFormData =
    useCallback((): ReviseReportInput => {
      const formData = getValues();
      return {
        orderId: order.id,
        orderingProviderId: formData.orderingProviderId,
        patientDateOfBirthPhi: formData.dateOfBirth,
        patientEthnicity: (safeEmptyToUndefined(formData.ethnicity) ??
          null) as Ethnicity | null,
        patientFirstNamePhi: formData.firstName.trim(),
        patientLastNamePhi: formData.lastName.trim(),
        reason: formData.revisionReason.trim(),
        labProcedureDatePhi: formData.dateOfBiopsy,
        ...(isClinicWorkflow
          ? { specimenId: formData.specimenId?.trim() ?? '' }
          : {}),
        metastaticProstateData: {
          psa: formData.baselinePsa!,
          tStage: formData.tStage,
        },
      } as ReviseReportInput;
    }, [getValues, order, isClinicWorkflow]);

  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state]);

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [showReviseAtLeastOneError, setShowReviseAtLeastOneError] =
    useState(false);

  const hasRelevantDirtyFields =
    hasSemanticFormChanges(
      currentValues,
      defaultValues,
      FIELDS_EXCLUDED_FROM_REVISE_ONE_CHECK,
    ) || hasOrderingProviderChanges;

  useEffect(() => {
    if (hasRelevantDirtyFields && showReviseAtLeastOneError) {
      setShowReviseAtLeastOneError(false);
    }
  }, [hasRelevantDirtyFields, showReviseAtLeastOneError]);

  const onSubmitClick = async () => {
    setShowReviseAtLeastOneError(false);
    const isValid = await trigger();
    if (!isValid) return;
    if (!hasRelevantDirtyFields) {
      setShowReviseAtLeastOneError(true);
      return;
    }
    setSubmitDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitDialogOpen(false);
    const input = getReviseReportInputFromFormData();
    const formData = new FormData();
    formData.append('action', OrderAction.REVISE_REPORT);
    formData.append('reviseReportInput', JSON.stringify(input));
    fetcher.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  };

  const handleCloseConfirmSubmitDialog = () => {
    setSubmitDialogOpen(false);
  };

  return (
    <Box
      component='form'
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmitClick();
      }}
      noValidate
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '2000px',
        maxHeight: '1500px',
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <LdtOrderFormHeader
        specimenType={SpecimenType.MetastaticProstateBiopsy}
        onClose={() => navigate(-1)}
        crossOrgId={order.crossOrgId}
        title='Revise Report'
      />
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        *Indicates a required field
      </Typography>
      {/* Scrollable content area */}
      <Box sx={{ flex: 1, overflow: 'auto', mt: 2 }}>
        {/* Ordering Provider Section */}
        <OrderingProviderFormSection
          control={control}
          errors={errors}
          trigger={trigger}
          ldtOrderRepository={ldtOrderRepository}
          selectedOrderingProvider={selectedOrderingProvider}
          {...orderingProviderSectionProps}
        />

        {/* Patient Information Section */}
        <PatientInformationFormSection control={control} errors={errors} />

        {/* Clinical Information Section */}
        <FormSectionCard
          header={<SectionHeader title='Clinical & Pathology Information' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='baselinePsa'
                  control={control}
                  render={({ field }) => {
                    return (
                      <DecimalInput
                        required
                        fullWidth
                        label='Most Recent Pre-Treatment PSA (ng/mL)'
                        value={field.value}
                        decimalPlaces={2}
                        min={FORM_CONSTANTS.PSA_MIN}
                        error={!!errors.baselinePsa}
                        validationText={errors.baselinePsa?.message}
                        warningText={warningsRef.current.baselinePsa}
                        onBlur={field.onBlur}
                        onChange={(value) => {
                          field.onChange(value);
                          debouncedPsaValidation();
                        }}
                      />
                    );
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='tStage'
                  control={control}
                  render={({ field }) => (
                    <SelectList
                      {...field}
                      required
                      label='Clinical T-stage'
                      labelId='t-stage-label'
                      value={field.value ?? undefined}
                      onChange={(event) => {
                        field.onChange(event);
                        void trigger('tStage');
                      }}
                      onBlur={field.onBlur}
                      options={getProstateBiopsyTStageOptions(true, false)}
                      validationText={errors.tStage?.message?.toString() ?? ''}
                    />
                  )}
                />
              </Box>
            </ResponsiveFormRow>

            {/* Date of Procedure & Specimen ID */}

            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='dateOfBiopsy'
                  control={control}
                  render={({ field }) => (
                    <FormDatePicker
                      label={SpecimenTypeUtils.getSpecimenTypeInputLabel(
                        SpecimenType.MetastaticProstateBiopsy,
                      )}
                      maxDate={new Date()}
                      value={field.value}
                      required
                      fullWidth
                      error={!!errors.dateOfBiopsy}
                      helperText={
                        errors.dateOfBiopsy?.message?.toString() ?? ' '
                      }
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                }}
              >
                {isClinicWorkflow && (
                  <Controller
                    name='specimenId'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        fullWidth
                        label='Specimen ID'
                        error={!!errors.specimenId}
                        helperText={errors.specimenId?.message || ' '}
                      />
                    )}
                  />
                )}
              </Box>
            </ResponsiveFormRow>
          </Box>
        </FormSectionCard>

        {/* Revision Reason */}
        <FormSectionCard
          header={<SectionHeader title='Revision Reason' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Controller
              name='revisionReason'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  multiline
                  rows={4}
                  placeholder='Type here...'
                  error={!!errors.revisionReason}
                  helperText={errors.revisionReason?.message || ' '}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 400 },
                    },
                  }}
                />
              )}
            />
          </Box>
        </FormSectionCard>
      </Box>
      {/* Fixed form actions at the bottom */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-end' },
          }}
        >
          <Button
            type='submit'
            variant='contained'
            color='primary'
            size='large'
            disabled={fetcher.state !== 'idle'}
            sx={{
              width: { xs: '100%', sm: '160px' },
              maxWidth: 350,
              minHeight: { xs: '48px', sm: 'auto' },
            }}
          >
            {fetcher.state !== 'idle' ? 'SUBMITTING...' : 'SUBMIT'}
          </Button>
          <Typography variant='body2' color='error' role='alert'>
            {showReviseAtLeastOneError
              ? 'You must revise at least 1 field.'
              : ''}
          </Typography>
        </Box>
      </Box>
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Confirm Revise Report?',
          subtitle: 'I have reviewed and confirmed the information is correct.',
          type: FeedbackType.Primary,
          icon: Caution,
        }}
        isDialogOpen={submitDialogOpen}
        onPrimaryButtonClick={handleConfirmSubmit}
        onSecondaryButtonClick={handleCloseConfirmSubmitDialog}
        handleDialogClose={handleCloseConfirmSubmitDialog}
      />
      {orderingProviderDialogProps.open && (
        <OrderingProviderForm
          open
          orderRepository={ldtOrderRepository}
          onClose={orderingProviderDialogProps.onClose}
          initialProvider={orderingProviderDialogProps.initialProvider}
          onSuccess={orderingProviderDialogProps.onSuccess}
          onDelete={orderingProviderDialogProps.onDelete}
        />
      )}
    </Box>
  );
};
